/**
 * L5: page visibility for privacy (03 CSO caveat: the iOS app switcher snapshots the last screen).
 * When the page goes away the shell drops revealed values, closes Large Type and raises a cover.
 * It listens to visibilitychange AND pagehide/pageshow, because iOS does not always deliver both,
 * and reports each transition once.
 *
 * Limit, stated plainly: iOS may take its snapshot before page script runs. The cover is best
 * effort; the fixed-length mask and the 30 s reveal timeout are what actually bound exposure.
 */
export interface VisibilityHandlers {
  hidden(): void;
  visible(): void;
}

export function watchVisibility(doc: EventTarget, win: EventTarget, isHidden: () => boolean, handlers: VisibilityHandlers): () => void {
  let state: "visible" | "hidden" = "visible";
  const set = (next: "visible" | "hidden") => {
    if (next === state) return;
    state = next;
    if (next === "hidden") handlers.hidden();
    else handlers.visible();
  };
  const onVis = () => set(isHidden() ? "hidden" : "visible");
  const onHide = () => set("hidden");
  const onShow = () => set("visible");
  doc.addEventListener("visibilitychange", onVis);
  win.addEventListener("pagehide", onHide);
  win.addEventListener("pageshow", onShow);
  return () => {
    doc.removeEventListener("visibilitychange", onVis);
    win.removeEventListener("pagehide", onHide);
    win.removeEventListener("pageshow", onShow);
  };
}
